import React, { useContext, useState } from 'react'

import { Icon } from '@navent-jobs/ui-kit/components/icon'
import { useFilters } from '@navent-jobs/ui-kit/hooks/filters'
import { Input } from '@navent-jobs/ui-kit/components/input'
import { Chip } from '@navent-jobs/ui-kit/components/chip'
import { ThemeContext } from 'styled-components'

import { useTranslation } from 'react-i18next'
import { DataLayers } from '../../../../../components/filter/gtm-events-filters'
import FiltersPlaceholder from '../../filters-section/FiltersPlaceholder'

import {
  LayerOneFilterSidebarComponent,
  LayerTwoFilterSidebarComponent,
  LayerTwoSidebarTitle,
  LayerOneSidebarFooter,
  SidebarMenu,
  RemoveFilter,
  RemoveAllFilters,
  ActiveFilter,
  IdleFilter,
  DisabledFilter,
  Facet,
  FilterIcon,
  TagFilterSidebarComponent,
  ActiveFilterTag,
  FilterName,
  EmptySearchText,
  LabelContainer,
} from './mixins'

import { pushFilterToRecentSearches } from '../utils/pushfilter'
import { useSearchKeyword } from '../../../../../hooks/search-keyword'
import features from '../../../../../constants/features-per-country'
import { SITE_ID } from '../../../../../constants'

const searchAvisosV2Enabled = features[SITE_ID || 'BMAR'].searchAvisosV2.enabled

const MakeIdleFilter = (filter, setFirstLayerFiltering, performFiltering) => {
  const { options, type, facets, icon, label } = filter

  const onClickFilter = withCheckbox => {
    if (withCheckbox) {
      DataLayers.filterApplied(true, label, 'Si')
      performFiltering(type, { value: facets[0].idSemantico || facets[0].id, label: facets[0].name })
    } else {
      setFirstLayerFiltering(filter)
    }
  }

  return (
    <IdleFilter onClick={() => onClickFilter(options && options.withCheckbox)}>
      <LabelContainer>
        <FilterIcon name={icon} size="16" color="#231F20" />
        {label}
      </LabelContainer>
      {options?.withLabelNew && (
        <Chip text="Nuevo" chipOptions={{ color: 'extra', skin: 'outline', variant: 'lighten', size: 'small' }} />
      )}
    </IdleFilter>
  )
}

const MakeDisabledFilter = filter => {
  const { icon, label } = filter

  return (
    <DisabledFilter>
      <FilterIcon name={icon} size="16" color="#C3C2C8" />
      {label}
    </DisabledFilter>
  )
}

const MakeActiveFilter = (filter, setSecondLayerFiltering) => {
  const { optionSelected, type } = filter

  return (
    optionSelected && (
      <ActiveFilterTag>
        <ActiveFilter>
          <FilterName>{optionSelected[0].name || optionSelected[0].value}</FilterName>
          <RemoveFilter onClick={() => setSecondLayerFiltering(type, null)}>
            <Icon name="icon-light-close" color="white" size="16" />
          </RemoveFilter>
        </ActiveFilter>
      </ActiveFilterTag>
    )
  )
}

const hasSearchInput = facetId => {
  return facetId === 'area' || facetId === 'provincia' || facetId === 'localidad'
}

export const FilterSidebar = ({
  urlService,
  routerMatch,
  allFilters,
  allAppliedFilters,
  loading,
  filterSettings,
  deleteAll,
  defaultFilters,
}) => {
  const { filters, performFiltering } = useFilters({
    allFilters,
    allAppliedFilters,
    urlService,
    routerMatch,
    filterSettings,
    defaultFilters,
    searchAvisosV2Enabled,
  })
  const [layerTwoOpened, setLayerTwoOpened] = useState(false)
  const [filterSelected, setFilterSelected] = useState()
  const [searchQueryFacet, setSearchQueryFacet] = useState<string | undefined>(undefined)
  const queryApplied = allAppliedFilters?.find(f => f.id === 'query')
  const { searchKeyword } = useSearchKeyword()
  const busquedaFilter = {
    type: 'busqueda',
    optionSelected: [
      {
        name: searchKeyword || queryApplied?.value?.replaceAll('-', ' '),
      },
    ],
  }
  const { colors } = useContext<{ colors }>(ThemeContext)

  const { t } = useTranslation()

  const focusSearchInput = () => {
    setTimeout(() => document.getElementById('search-facets')?.focus(), 500)
  }

  const setFirstLayerFiltering = filter => {
    setFilterSelected(filter)
    setLayerTwoOpened(true)
    focusSearchInput()
    setSearchQueryFacet('')
  }

  const setSecondLayerFiltering = (type, facetSelected) => {
    performFiltering(type, facetSelected)
    setLayerTwoOpened(false)
    pushFilterToRecentSearches([{ type, facetSelected }], allAppliedFilters)
  }

  const secondLayerFiltering = (filter, { value, label }) => {
    DataLayers.filterApplied(true, filter.label, label)
    setSecondLayerFiltering(filter.type, { value, label })
  }

  const placeholderString = facet => {
    switch (facet?.type) {
      case 'area':
        return t('filtros.placeholderSearchFacet.area')
      case 'provincia':
        return t('filtros.placeholderSearchFacet.provincia')
      case 'localidad':
        return t('filtros.placeholderSearchFacet.localidad')
      default:
        return ''
    }
  }

  const facetsSelectedFiltered = filterSelected?.facets?.filter(
    p =>
      !searchQueryFacet ||
      p.name
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/´/g, '')
        .includes(searchQueryFacet.toLowerCase()) ||
      p.name.toLowerCase().includes(searchQueryFacet.toLocaleLowerCase())
  )

  if (!filters) {
    return null
  }

  if (loading) {
    return <FiltersPlaceholder />
  }

  return (
    <>
      <div>
        <TagFilterSidebarComponent>
          {queryApplied && MakeActiveFilter(busquedaFilter, setSecondLayerFiltering)}
          {filters.map(filter => {
            const { optionSelected } = filter

            return optionSelected && MakeActiveFilter(filter, setSecondLayerFiltering)
          })}
        </TagFilterSidebarComponent>

        <LayerOneFilterSidebarComponent>
          {filters.map(filter => {
            const { facets, optionSelected } = filter
            const thereIsAnOptionSelected = optionSelected && optionSelected.length > 0
            const thereAreFacets = facets && facets.length > 0
            const filterDisabled = !thereIsAnOptionSelected && !thereAreFacets

            if (filterDisabled) {
              return MakeDisabledFilter(filter)
            }

            return !optionSelected && MakeIdleFilter(filter, setFirstLayerFiltering, performFiltering)
          })}
        </LayerOneFilterSidebarComponent>

        <SidebarMenu
          position="left"
          closeType="backFilter"
          visible={layerTwoOpened}
          handleClose={() => setLayerTwoOpened(false)}
        >
          <LayerTwoSidebarTitle>
            <FilterIcon name={filterSelected && filterSelected.icon} size="20" color={colors.secondary.normal} />
            {filterSelected && filterSelected.label}
          </LayerTwoSidebarTitle>

          <LayerTwoFilterSidebarComponent>
            {hasSearchInput(filterSelected?.type) && (
              <Input
                id="search-facets"
                name="search-facets"
                type="text"
                value={searchQueryFacet}
                onChange={e => {
                  const { value } = e.target
                  setSearchQueryFacet(value)
                }}
                placeholder={t('filtros.placeholderSearchFacet.text', { facet: placeholderString(filterSelected) })}
                fieldOptions={{
                  variant: 'darken',
                  size: 'medium',
                  before: <Icon name="icon-light-search" color="#0a26ee" size="24" />,
                }}
              />
            )}
            {filterSelected && filterSelected.facets && facetsSelectedFiltered.length > 0 ? (
              facetsSelectedFiltered.map(facet => {
                if ((facet.idSemantico || facet.id) === 'desde-cualquier-lugar-del-mundo') {
                  return null
                }
                return (
                  <Facet
                    key={`${facet.idSemantico || facet.id}`}
                    onClick={() =>
                      secondLayerFiltering(filterSelected, { value: facet.idSemantico || facet.id, label: facet.name })
                    }
                  >
                    {facet.name} <span>({facet.quantity})</span>
                  </Facet>
                )
              })
            ) : (
              <EmptySearchText>No se encontraron resultados</EmptySearchText>
            )}
          </LayerTwoFilterSidebarComponent>
        </SidebarMenu>

        <LayerOneSidebarFooter>
          {deleteAll ? (
            <RemoveAllFilters onClick={() => performFiltering('all', null)}>
              <Icon name="icon-light-trash-2" color={colors.primary.normal} size="24" />
            </RemoveAllFilters>
          ) : (
            <Icon name="icon-light-trash-2" color="#C3C2C8" size="24" />
          )}
        </LayerOneSidebarFooter>
      </div>
    </>
  )
}
